import { Timestamp } from "@bufbuild/protobuf";
import { Code, ConnectError } from "@connectrpc/connect";
import {
  CardChargeOrder,
  CardChargeOrder_Status,
  CreditCard,
  MypaymentChargeOrder,
  MypaymentChargeOrder_Status,
} from "schema/gen/es/chiikipoint/model/v2/model_pb";
import {
  type AddCreditCardRequest,
  AddCreditCardResponse,
  type ChargeByCreditCardRequest,
  ChargeByCreditCardResponse,
  type CreateMypaymentChargeOrderRequest,
  CreateMypaymentChargeOrderResponse,
  type DeleteCreditCardRequest,
  DeleteCreditCardResponse,
  GetAvailableAwaitingPaymentMypaymentChargeOrderResponse,
  GetAvailableCardChargeOrderResponse,
  GetCreditCardsResponse,
  type SetDefaultCreditCardRequest,
  SetDefaultCreditCardResponse,
} from "schema/gen/es/chiikipoint/wallet/v2/service_pb";
import { consumeOsakiChargeBonusQuantity } from "./wallet-service-campaign";

const mockCards = new Map<string, CreditCard>([
  [
    "mock-card-1",
    new CreditCard({
      gmoCardId: "mock-card-1",
      maskedCardNo: "410000******0100",
      cardBrand: "VISA",
      expiryMonth: "12",
      expiryYear: "2026",
      holderName: "TARO YAMADA",
    }),
  ],
]);

let defaultGmoCardId = "mock-card-1";
let latestAwaitingMypaymentOrder: MypaymentChargeOrder | null = null;
const latestAvailableCardChargeOrder: CardChargeOrder | null =
  createInitialAvailableCardChargeOrder();

function createInitialAvailableCardChargeOrder() {
  if (import.meta.env.VITE_MOCK_AVAILABLE_CARD_CHARGE_ORDER !== "true") {
    return null;
  }

  return createAvailableCardChargeOrder({
    price: BigInt(5000),
    quantity: BigInt(1),
    gmoCardId: defaultGmoCardId,
  });
}

function createAvailableCardChargeOrder({
  price,
  quantity,
  gmoCardId,
}: {
  price: bigint;
  quantity: bigint;
  gmoCardId: string;
}) {
  const now = new Date();

  return new CardChargeOrder({
    gmoOrderId: "mock-card-charge-order-id",
    userId: "mock-user-id",
    price,
    quantity,
    status: CardChargeOrder_Status.AWAITING_3DS,
    gmoAccessId: "mock-card-access-id",
    gmoCardId,
    createdAt: Timestamp.fromDate(now),
    updatedAt: Timestamp.fromDate(now),
  });
}

export function handleGetCreditCards() {
  return new GetCreditCardsResponse({
    cards: Object.fromEntries(mockCards),
    defaultGmoCardId,
  });
}

export function handleAddCreditCard(req: AddCreditCardRequest) {
  const id = `mock-card-${mockCards.size + 1}`;
  mockCards.set(
    id,
    new CreditCard({
      gmoCardId: id,
      maskedCardNo: "410000******0100",
      cardBrand: "VISA",
      expiryMonth: "12",
      expiryYear: "2026",
      holderName: "TARO YAMADA",
      billingAddress: req.billingAddress
        ? {
            cardVerificationId: "mock-verification-id",
            name: req.billingAddress.name,
            line1: req.billingAddress.line1,
            line2: req.billingAddress.line2,
            line3: req.billingAddress.line3,
            city: req.billingAddress.city,
            state: req.billingAddress.state,
            postCode: req.billingAddress.postCode,
          }
        : undefined,
    }),
  );

  if (req.setAsDefault) {
    defaultGmoCardId = id;
  }

  return new AddCreditCardResponse({
    cardVerificationId: "00000000-0000-4000-8000-000000000001",
  });
}

export function handleChargeByCreditCard(req: ChargeByCreditCardRequest) {
  consumeOsakiChargeBonusQuantity(req.quantity);
  return new ChargeByCreditCardResponse({
    cardChargeOrderId: "mock-card-charge-order-id",
  });
}

export function handleCreateMypaymentChargeOrder(
  req: CreateMypaymentChargeOrderRequest,
) {
  consumeOsakiChargeBonusQuantity(req.quantity);

  const now = new Date();
  const barcodeAvailableUntil = new Date(now);
  barcodeAvailableUntil.setDate(barcodeAvailableUntil.getDate() + 1);
  barcodeAvailableUntil.setHours(23, 59, 59, 999);

  latestAwaitingMypaymentOrder = new MypaymentChargeOrder({
    gmoOrderId: `mock-mypayment-${Date.now()}`,
    userId: "mock-user-id",
    price: req.price,
    quantity: req.quantity,
    status: MypaymentChargeOrder_Status.AWAITING_PAYMENT,
    gmoAccessId: "mock-mypayment-access-id",
    barcodeUrl: "https://example.com/mock-mypayment-barcode",
    barcodeAvailableUntil: Timestamp.fromDate(barcodeAvailableUntil),
    createdAt: Timestamp.fromDate(now),
    updatedAt: Timestamp.fromDate(now),
    transactionIds: [],
  });

  return new CreateMypaymentChargeOrderResponse({
    order: latestAwaitingMypaymentOrder,
  });
}

export function handleGetAvailableAwaitingPaymentMypaymentChargeOrder() {
  if (!latestAwaitingMypaymentOrder) {
    throw new ConnectError(
      "available mypayment charge order not found",
      Code.NotFound,
    );
  }

  return new GetAvailableAwaitingPaymentMypaymentChargeOrderResponse({
    order: latestAwaitingMypaymentOrder,
  });
}

export function handleGetAvailableCardChargeOrder() {
  if (!latestAvailableCardChargeOrder) {
    throw new ConnectError(
      "available card charge order not found",
      Code.NotFound,
    );
  }

  return new GetAvailableCardChargeOrderResponse({
    order: latestAvailableCardChargeOrder,
  });
}

export function handleDeleteCreditCard(req: DeleteCreditCardRequest) {
  mockCards.delete(req.gmoCardId);
  if (defaultGmoCardId === req.gmoCardId) {
    defaultGmoCardId = mockCards.keys().next().value ?? "";
  }
  return new DeleteCreditCardResponse();
}

export function handleSetDefaultCreditCard(req: SetDefaultCreditCardRequest) {
  defaultGmoCardId = req.gmoCardId;
  return new SetDefaultCreditCardResponse({
    card: mockCards.get(req.gmoCardId),
  });
}
